import React, { useEffect, useState } from 'react';
import type { StudentNoteItem } from '../../../types/orchestrator';
import { useEscapeToClose } from '../../../hooks/useEscapeToClose';
import ConfirmModal from '../ConfirmModal/ConfirmModal';
import { StudentNoteSummaryService } from '../../../services/StudentNoteSummaryService';
import StudentNotesComposeForm from './StudentNotesComposeForm';
import StudentNotesHistoryPanel from './StudentNotesHistoryPanel';
import StudentNotesModalHeader from './StudentNotesModalHeader';
import StudentNotesModalTabs from './StudentNotesModalTabs';
import StudentNotesStudentInfo from './StudentNotesStudentInfo';
import StudentNotesSummaryPanel from './StudentNotesSummaryPanel';
import type { StudentNotesModalTabKey } from './studentNotesModal.constants';
import styles from './StudentNotesModal.module.css';

interface StudentNotesModalProps {
  open: boolean;
  studentName: string;
  notes: StudentNoteItem[];
  historyFooter?: React.ReactNode;
  summary?: string | null;
  showSummary?: boolean;
  value: string;
  loading?: boolean;
  width?: number;
  onChange: (value: string) => void;
  onCancel: () => void;
  onSubmit: () => void;
  onDeleteNote?: (noteId: number) => Promise<void>;
  canWriteObservations?: boolean;
  studentId?: number | null;
  onSummaryGenerated?: (summary: string) => void;
  title?: string;
  closeLabel?: string;
  showHistory?: boolean;
  fieldId?: string;
  fieldLabel?: string;
  placeholder?: string;
  minRows?: number;
  maxRows?: number;
  skipValidation?: boolean;
}

const DEFAULT_WIDTH_PX = Math.round(840 * 1.3);

const StudentNotesModal: React.FC<StudentNotesModalProps> = ({
  open,
  studentName,
  notes,
  historyFooter,
  summary,
  showSummary = true,
  value,
  loading = false,
  width = DEFAULT_WIDTH_PX,
  onChange,
  onCancel,
  onSubmit,
  onDeleteNote,
  canWriteObservations = true,
  studentId = null,
  onSummaryGenerated,
  title = 'Observações',
  closeLabel = 'Fechar observações',
  showHistory = true,
  fieldId,
  fieldLabel = 'Nova observação',
  placeholder = 'Escreva aqui a observação que deseja enviar.',
  minRows,
  maxRows,
  skipValidation = false,
}) => {
  const [activeTab, setActiveTab] = useState<StudentNotesModalTabKey>(showSummary ? 'summary' : 'history');
  const [confirmGenerateOpen, setConfirmGenerateOpen] = useState(false);
  const [generatingSummary, setGeneratingSummary] = useState(false);
  const [generatedSummary, setGeneratedSummary] = useState<string | null>(null);
  const busy = loading || generatingSummary;
  const canGenerateSummary = showSummary && canWriteObservations && studentId != null && studentId > 0;

  useEscapeToClose(() => {
    if (!confirmGenerateOpen) {
      onCancel();
    }
  }, open && !busy && !confirmGenerateOpen);

  useEffect(() => {
    if (!open) {
      return;
    }

    const previousBodyOverflow = document.body.style.overflow;
    const previousHtmlOverflow = document.documentElement.style.overflow;
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previousBodyOverflow;
      document.documentElement.style.overflow = previousHtmlOverflow;
    };
  }, [open]);

  useEffect(() => {
    if (open) {
      setActiveTab(showSummary ? 'summary' : 'history');
      setConfirmGenerateOpen(false);
      setGeneratedSummary(null);
    }
  }, [open, showSummary, studentId]);

  const requestClose = () => {
    if (busy || confirmGenerateOpen) {
      return;
    }

    onCancel();
  };

  const handleGenerateSummary = async () => {
    if (!canGenerateSummary || studentId == null || generatingSummary) {
      return;
    }

    setGeneratingSummary(true);
    try {
      const summary = await StudentNoteSummaryService.generate(studentId);
      setGeneratedSummary(summary);
      setActiveTab('summary');
      onSummaryGenerated?.(summary);
      setConfirmGenerateOpen(false);
    } catch (error) {
      alert(error instanceof Error ? error.message : 'Não foi possível gerar o resumo das observações.');
    } finally {
      setGeneratingSummary(false);
    }
  };

  if (!open) {
    return null;
  }

  return (
    <>
    <div className={styles.overlay} role="presentation" onMouseDown={requestClose}>
      <section
        className={[styles.modal, showHistory ? '' : styles.modalCompact].filter(Boolean).join(' ')}
        style={{ '--student-notes-modal-width': `${width}px` } as React.CSSProperties}
        role="dialog"
        aria-modal="true"
        aria-labelledby="student-notes-modal-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <StudentNotesModalHeader title={title} closeLabel={closeLabel} loading={busy} onClose={requestClose} />
        <StudentNotesStudentInfo
          studentName={studentName}
          showGenerateSummary={canGenerateSummary}
          generatingSummary={generatingSummary}
          generateDisabled={loading}
          onGenerateSummary={() => setConfirmGenerateOpen(true)}
        />

        {showHistory && (
          <StudentNotesModalTabs
            activeTab={showSummary ? activeTab : 'history'}
            showSummary={showSummary}
            onTabChange={setActiveTab}
          >
            {showSummary && activeTab === 'summary' ? (
              <StudentNotesSummaryPanel value={generatedSummary ?? summary} />
            ) : (
              <StudentNotesHistoryPanel
                notes={notes}
                footer={historyFooter}
                onDeleteNote={canWriteObservations ? onDeleteNote : undefined}
              />
            )}
          </StudentNotesModalTabs>
        )}

        <StudentNotesComposeForm
          value={value}
          loading={busy}
          readOnly={!canWriteObservations}
          fieldId={fieldId}
          fieldLabel={fieldLabel}
          placeholder={placeholder}
          minRows={minRows}
          maxRows={maxRows}
          skipValidation={skipValidation}
          onChange={onChange}
          onCancel={requestClose}
          onSubmit={onSubmit}
        />
      </section>
    </div>
      <ConfirmModal
        open={confirmGenerateOpen}
        variant="warning"
        icon="auto_awesome"
        title="Gerar resumo?"
        message="Deseja gerar o resumo das observações desta aluna? O resumo atual será substituído."
        confirmLabel="Sim"
        cancelLabel="Cancelar"
        confirmIcon="auto_awesome"
        loading={generatingSummary}
        loadingLabel="Gerando..."
        onCancel={() => {
          if (!generatingSummary) {
            setConfirmGenerateOpen(false);
          }
        }}
        onConfirm={() => {
          void handleGenerateSummary();
        }}
      />
    </>
  );
};

export default StudentNotesModal;
